import { useState } from "react"
import Toggle from "@ciq/shared/components/toggle"
import { Button } from "@ciq/shared/components/buttons"
import { EntryModal } from "@ciq/shared/components/modal"

/**
 * @typedef {Object} JobSettings
 * @property {boolean} allow_student_job - Enable Student job
 * @property {boolean} allow_class_attendance_job - Enable Class Attendance job
 * @property {boolean} allow_behavior_job - Enable Weekly Behavior job
 * @property {boolean} allow_facility_usage_job - Enable Facility Usage job
 * @property {boolean} allow_event_attendance_job - Enable Event Attendance job
 */

/**
 * @typedef {Object} JobEnableSectionProps
 * @property {JobSettings} settings - Current job enable settings
 * @property {(key: string, value: boolean) => void} onChange - Called when a toggle changes
 * @property {() => void} onSave - Called when Update button is clicked
 * @property {boolean} isLoading - Whether a save operation is in progress
 */

// NOTE: Keep in sync with JobOptions.dependsOn in view.js
const JOB_DEPENDENCIES = {
  allow_class_attendance_job: ["allow_student_job"],
  allow_behavior_job: ["allow_student_job"],
}

const JOB_TOGGLES = [
  { key: "allow_facility_usage_job", label: "Facility Usage", testId: "toggle-facility-usage" },
  { key: "allow_student_job", label: "Student", testId: "toggle-student" },
  { key: "allow_class_attendance_job", label: "Class Attendance", testId: "toggle-class-attendance", indent: true },
  { key: "allow_behavior_job", label: "Weekly Behavior", testId: "toggle-behavior", indent: true },
  { key: "allow_event_attendance_job", label: "Event Attendance", testId: "toggle-event-attendance" },
]

const STUDENT_DEPENDENTS = Object.keys(JOB_DEPENDENCIES)

/**
 * Section for enabling/disabling Spark job types.
 * Displays toggles for each job type with an Update button to save changes.
 * Shows a confirmation modal when disabling Student while dependent jobs are active.
 *
 * @param {JobEnableSectionProps} props - Component props
 * @returns {JSX.Element} The job enable section component
 */
export function JobEnableSection({ settings, onChange, onSave, isLoading }) {
  const [showDisableStudentModal, setShowDisableStudentModal] = useState(false)

  /**
   * Intercepts toggle changes to show a confirmation modal when disabling Student
   * while dependent jobs (Class Attendance, Weekly Behavior) are still active.
   *
   * @param {string} key - The settings key being toggled
   * @param {boolean} value - The new toggle value
   */
  function handleToggleChange(key, value) {
    if (key === "allow_student_job" && value === false) {
      const activeDependents = STUDENT_DEPENDENTS.filter((dep) => settings[dep])
      if (activeDependents.length > 0) {
        setShowDisableStudentModal(true)
        return
      }
    }
    onChange(key, value)
  }

  /**
   * Confirms disabling the Student job and all its active dependent jobs.
   */
  function handleConfirmDisableStudent() {
    onChange("allow_student_job", false)
    STUDENT_DEPENDENTS.forEach((dep) => {
      if (settings[dep]) {
        onChange(dep, false)
      }
    })
    setShowDisableStudentModal(false)
  }

  return (
    <div className="spark-job-enable-section">
      <div className="spark-job-enable-toggles">
        {JOB_TOGGLES.map(({ key, label, testId, indent }) => {
          const deps = JOB_DEPENDENCIES[key]
          const isDependencyDisabled = deps?.some((dep) => !settings[dep]) ?? false
          const isDisabled = isLoading || isDependencyDisabled

          return (
            <div
              key={key}
              className={`spark-job-enable-toggle${indent ? " spark-job-enable-toggle--indent" : ""}${isDependencyDisabled ? " spark-job-enable-toggle--disabled" : ""}`}
              data-testid={testId}
            >
              <span onClick={() => !isDisabled && handleToggleChange(key, !settings[key])}>{label}</span>
              <Toggle
                checked={settings[key]}
                disabled={isDisabled}
                onChange={(checked) => handleToggleChange(key, checked)}
              />
            </div>
          )
        })}
      </div>
      <div className="spark-job-enable-actions">
        <Button variant="primary" disabled={isLoading} onClick={onSave}>
          Update
        </Button>
      </div>
      <EntryModal
        show={showDisableStudentModal}
        title="Disable Student Job?"
        onHide={() => setShowDisableStudentModal(false)}
        buttons={{
          secondary: { title: "Cancel", onClick: () => setShowDisableStudentModal(false) },
          danger: { title: "Continue", onClick: handleConfirmDisableStudent },
        }}
      >
        <p>
          Disabling Student will also disable{" "}
          {STUDENT_DEPENDENTS.filter((dep) => settings[dep])
            .map((dep) => JOB_TOGGLES.find(({ key }) => key === dep)?.label)
            .join(" and ")}
          . Continue?
        </p>
      </EntryModal>
    </div>
  )
}
